export interface OverlayTool {
  name: string
  label: string
}

export interface OverlayGroup {
  label: string
  tools: OverlayTool[]
}

export const OVERLAY_GROUPS: OverlayGroup[] = [
  {
    label: 'Lines',
    tools: [
      { name: 'straightLine', label: 'Straight Line' },
      { name: 'segment', label: 'Segment' },
      { name: 'rayLine', label: 'Ray Line' },
      { name: 'horizontalStraightLine', label: 'Horizontal Line' },
      { name: 'horizontalSegment', label: 'Horizontal Segment' },
      { name: 'horizontalRayLine', label: 'Horizontal Ray' },
      { name: 'verticalStraightLine', label: 'Vertical Line' },
      { name: 'verticalSegment', label: 'Vertical Segment' },
      { name: 'verticalRayLine', label: 'Vertical Ray' },
      { name: 'priceLine', label: 'Price Line' },
    ],
  },
  {
    label: 'Channels',
    tools: [
      { name: 'parallelStraightLine', label: 'Parallel Channel' },
      { name: 'priceChannelLine', label: 'Price Channel' },
      { name: 'fibonacciLine', label: 'Fibonacci Retracement' },
    ],
  },
  {
    label: 'Position',
    tools: [
      { name: 'longPositionTool', label: 'Long Position' },
      { name: 'shortPositionTool', label: 'Short Position' },
      // Two-click sizing plan: first click = entry, second = stop; direction
      // and share size are inferred (see positionPlanOverlay.ts).
      { name: 'positionPlan', label: 'Position Plan' },
    ],
  },
  {
    label: 'Measure',
    tools: [
      // The ruler lives IN this group, as it does in the reference app. It is
      // the one ephemeral tool in the catalog — never persisted; see
      // ChartManager.handleOverlayDrawEnd's `name === 'measure'` branch.
      { name: 'measure', label: 'Measure' },
      { name: 'dateRange', label: 'Date Range' },
      { name: 'priceRange', label: 'Price Range' },
      { name: 'datePriceRange', label: 'Date & Price Range' },
    ],
  },
  {
    label: 'Arrows',
    tools: [
      { name: 'arrow', label: 'Arrow' },
      { name: 'arrowMarkUp', label: 'Arrow Mark Up' },
      { name: 'arrowMarkDown', label: 'Arrow Mark Down' },
      { name: 'arrowMarkLeft', label: 'Arrow Mark Left' },
      { name: 'arrowMarkRight', label: 'Arrow Mark Right' },
    ],
  },
  {
    label: 'Shapes',
    tools: [
      { name: 'rect', label: 'Rectangle' },
      { name: 'circle', label: 'Circle' },
      { name: 'ellipse', label: 'Ellipse' },
      { name: 'triangle', label: 'Triangle' },
      { name: 'polygon', label: 'Polygon' },
      { name: 'polyline', label: 'Polyline' },
      { name: 'simpleAnnotation', label: 'Annotation' },
      { name: 'simpleTag', label: 'Tag' },
      // NOTE: 'text' and 'arc' were listed here but are klinecharts *figures*,
      // not overlay templates — selecting them was a silent no-op. Removed
      // rather than left advertised. 'text' belongs in Text & Notes and needs
      // the `extendData` persistence change to store its string.
    ],
  },
]

export const ALL_OVERLAYS: OverlayTool[] = OVERLAY_GROUPS.flatMap((g) => g.tools)

const LABEL_BY_NAME = new Map(ALL_OVERLAYS.map((t) => [t.name, t.label]))

/** A tool's display label, falling back to its overlay name. */
export function overlayLabel(name: string): string {
  return LABEL_BY_NAME.get(name) ?? name
}
